import api from './axiosApi'

export interface SearchablePlan {
  id: string
  name: string
  description: string
  hierarchy: string
  hierarchyId?: string // The leaf hierarchy node ID
  seasonId?: string
  year?: number
}

export interface PlanSearchParams {
  q?: string // Search query (undefined = get latest)
  seasonId?: string
  orderBy?: string // e.g., '-modified_at'
  offset?: number // Pagination
  limit?: number // Items per page (default 20)
}

export interface PlanSearchResponse {
  plans: SearchablePlan[]
  totalCount: number
  hasMore: boolean // For infinite scroll
  nextOffset: number | null
}

// Backend types
interface PlanSummary {
  id: string
  name: string
  plan_type: 'SEASONAL' | 'CORE'
  status: string
  season_id: string | null
  season: { id: string; name: string } | null
  fiscal_year: number | null
  hierarchy_id: string | null
  hierarchy: { id: string; code: string; name: string; level: string } | null
  line_count: number
  created_at: string
  modified_at: string | null
}

interface PaginationMeta {
  skip: number
  limit: number
  total: number
  has_more: boolean
}

interface PaginatedPlansResponse {
  data: PlanSummary[]
  pagination: PaginationMeta
}

// Map backend PlanSummary to frontend SearchablePlan
function mapPlanSummaryToSearchable(plan: PlanSummary): SearchablePlan {
  return {
    id: plan.id,
    name: plan.name,
    description: '', // Not in API response
    hierarchy: plan.hierarchy?.name ?? '',
    hierarchyId: plan.hierarchy_id ?? undefined,
    seasonId: plan.season_id ?? undefined,
    year: plan.fiscal_year ?? undefined,
  }
}

/**
 * Search plans by query and optionally filter by plan type
 * When query is empty/undefined, returns latest plans ordered by modified_at
 * @param params - Search parameters including pagination
 * @returns Promise with search results and pagination info
 */
export async function searchPlans(
  params: PlanSearchParams = {}
): Promise<PlanSearchResponse> {
  const {
    q,
    seasonId,
    orderBy = '-modified_at',
    offset = 0,
    limit = 20,
  } = params

  const response = await api.get<PaginatedPlansResponse>('/plans', {
    params: {
      q: q || undefined, // Omit if empty to get latest plans
      season_id: seasonId || undefined,
      order_by: orderBy,
      offset,
      limit,
    },
  })

  const { pagination } = response.data
  const nextOffset = pagination.has_more
    ? pagination.skip + pagination.limit
    : null

  return {
    plans: response.data.data.map(mapPlanSummaryToSearchable),
    totalCount: pagination.total,
    hasMore: pagination.has_more,
    nextOffset,
  }
}
